import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Icon, Link, Paper, Tooltip } from '@mui/material';

import { SystemNotice } from '../../models/systemNotice/systemNotice';
import { getSystemNoticeMeta } from '../../content/systemNoticeTypes';
import { systemNoticesService } from '../../services/system-notices.service';
import { getReadSystemNoticeIds } from '../../services/utils/systemNoticesStorage';

import ProgressBar from '../controls/ProgressBar';
import ErrorMessage from '../controls/ErrorMessage';
import SystemNoticesDialog from './SystemNoticesDialog';

const OverzichtMeldingen: React.FC = () => {
  const { accounts } = useMsal();

  const [notices, setNotices] = useState<SystemNotice[]>([]);
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  const userKey = accounts[0]?.username ?? null;

  const fetchNotices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await systemNoticesService.getActiveNotices();
      setNotices(result);
      setReadIds(getReadSystemNoticeIds(userKey));
    }
    catch (err: any) {
      setError(err?.message || 'Er is een fout opgetreden');
    }
    finally {
      setLoading(false);
    }
  }, [userKey]);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  const openViewer = useCallback((id: string) => {
    setNotices((current) => {
      const idx = current.findIndex((notice) => notice.id === id);
      setViewerIndex(idx < 0 ? 0 : idx);
      return current;
    });
    setViewerOpen(true);
  }, []);

  const columns = useMemo<GridColDef<SystemNotice>[]>(() => [
    {
      field: 'read',
      headerName: 'Gelezen',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<SystemNotice>) => {
        const isRead = readIds.has(params.row.id);
        return (
          <Tooltip title={isRead ? 'Gelezen' : 'Niet gelezen'}>
            <Icon sx={{ fontSize: 22, color: 'text.secondary' }}>
              {isRead ? 'bookmark' : 'bookmark_border'}
            </Icon>
          </Tooltip>
        );
      },
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<SystemNotice>) => {
        const meta = getSystemNoticeMeta(params.row.type);
        return (
          <Icon sx={{ color: meta.color }} aria-label={meta.label} title={meta.label}>
            {meta.icon}
          </Icon>
        );
      },
    },
    {
      field: 'title',
      headerName: 'Titel',
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<SystemNotice>) => (
        <Link
          component="button"
          type="button"
          underline="hover"
          color="secondary"
          onClick={() => openViewer(params.row.id)}
          sx={{ textAlign: 'left' }}
        >
          {params.row.title}
        </Link>
      ),
    },
  ], [openViewer, readIds]);

  return (
    <>
      <div className="grid-responsive" style={{ display: 'flex', flexDirection: 'column' }}>
        <Paper sx={{ width: '100%' }}>
          <DataGrid
            rows={notices}
            columns={columns}
            getRowId={(row) => row.id}
            hideFooter
            autoHeight
            disableColumnFilter
            disableColumnMenu
            disableColumnSorting
            disableRowSelectionOnClick
            sx={{ border: 0 }}
          />
        </Paper>
      </div>

      {loading && <ProgressBar />}
      {error && <ErrorMessage message={error} />}

      {/* Bekijk-modus: toont alle meldingen zonder rekening te houden met de LS-leesstatus. */}
      <SystemNoticesDialog
        open={viewerOpen}
        notices={notices}
        initialIndex={viewerIndex}
        onClose={() => setViewerOpen(false)}
      />
    </>
  );
};

export default OverzichtMeldingen;
